import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../Shared/axiosInstance.js";
import CustomDatePicker from "../../CustomDate/CustomDatePicker.jsx";

export default function CreateSupplierOpenBalance() {
  const API_BASE = "/SupplierOpeningBalance";
  const SUPPLIERS_API = "/Supplier/all";
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // حالة الـ Modal
  const [showModal, setShowModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);

  // جلب الموردين عند فتح الـ modal أو تغيير البحث
  useEffect(() => {
    if (!showModal) return;

    const fetchSuppliers = async () => {
      try {
        setLoadingSuppliers(true);
        const { data } = await axiosInstance.get(SUPPLIERS_API);
        const allSuppliers = data.data || [];
        
        // فلترة حسب البحث
        const filtered = supplierSearch
          ? allSuppliers.filter(s => 
              s.name.toLowerCase().includes(supplierSearch.toLowerCase()) ||
              s.phone.includes(supplierSearch)
            )
          : allSuppliers;
        
        setSuppliers(filtered);
      } catch (err) {
        console.error("Error fetching suppliers:", err);
        Swal.fire("خطأ", "فشل في تحميل الموردين", "error");
      } finally {
        setLoadingSuppliers(false);
      }
    };

    const delay = setTimeout(fetchSuppliers, 300);
    return () => clearTimeout(delay);
  }, [supplierSearch, showModal]);

  const formik = useFormik({
    initialValues: {
      items: [{ 
        supplierId: "", 
        supplierName: "", 
        amount: 0,
        balanceDate: new Date().toISOString().split("T")[0],
        notes: ""
      }],
    },
    validationSchema: Yup.object({
      items: Yup.array().of(
        Yup.object({
          supplierId: Yup.string().required("المورد مطلوب"),
          amount: Yup.number()
            .required("المبلغ مطلوب")
            .min(0, "المبلغ لا يمكن أن يكون سالباً"),
          balanceDate: Yup.string().required("التاريخ مطلوب"),
          notes: Yup.string().max(500, "الملاحظات لا تتجاوز 500 حرف"),
        })
      ),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          items: values.items.map((item) => ({
            supplierId: item.supplierId,
            amount: parseFloat(item.amount) || 0,
            balanceDate: new Date(item.balanceDate).toISOString(),
            notes: item.notes.trim() || null,
          })),
        };

        console.log("Sending payload:", payload);

        const { data } = await axiosInstance.post(API_BASE, payload);

        await Swal.fire({
          title: "تم بنجاح!",
          text: data.message || "تم إضافة الرصيد الافتتاحي للموردين بنجاح",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/balance-suppliers");
        formik.resetForm();
      } catch (err) {
        console.error("Error:", err);
        Swal.fire(
          "خطأ",
          err.response?.data?.message || "حدث خطأ، الرجاء المحاولة مرة أخرى",
          "error"
        );
      }
    },
  });

  const addNewItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      { 
        supplierId: "", 
        supplierName: "", 
        amount: 0,
        balanceDate: new Date().toISOString().split("T")[0],
        notes: ""
      },
    ]);
  };

  const removeItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue(
      "items",
      newItems.length ? newItems : [{ 
        supplierId: "", 
        supplierName: "", 
        amount: 0,
        balanceDate: new Date().toISOString().split("T")[0],
        notes: ""
      }]
    );
  };

  const openSupplierModal = (index) => {
    setCurrentItemIndex(index);
    setShowModal(true);
    setSupplierSearch("");
  };

  const selectSupplier = (supplier) => {
    if (currentItemIndex === null) return;

    formik.setFieldValue(`items[${currentItemIndex}].supplierId`, supplier.id);
    formik.setFieldValue(
      `items[${currentItemIndex}].supplierName`,
      `${supplier.name} - ${supplier.phone}`
    );

    setShowModal(false);
    setCurrentItemIndex(null);
  };

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container py-5" style={{ maxWidth: "1000px" }}>
        
        {/* الرأس */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
              <i className="bi bi-clipboard-data me-2"></i>إضافة رصيد افتتاحي للموردين
            </h2>
            <small style={{ color: "#64748b", fontSize: "14px" }}>
              تعيين الأرصدة الافتتاحية للموردين
            </small>
          </div>
          <button
            type="button"
            onClick={() => navigate("/suppliers")}
            style={{
              background: "#64748b",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            <i className="bi bi-arrow-left me-1"></i> عودة
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          
          {/* قسم الموردين */}
          <div style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-people me-2"></i>الموردين ({formik.values.items.length})
              </h5>
              <button
                type="button"
                onClick={addNewItem}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> إضافة مورد
              </button>
            </div>

            {formik.values.items.map((item, index) => (
              <div key={index} style={itemCardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ color: "#2563eb", fontWeight: "600", fontSize: "15px" }}>
                    المورد رقم {index + 1}
                  </span>
                  {formik.values.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      style={{
                        background: "#dc2626",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        border: "none",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <i className="bi bi-trash me-1"></i> إزالة
                    </button>
                  )}
                </div>

                <div className="mb-3">
                  <label style={labelStyle}>المورد</label>
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      value={item.supplierName || "لم يتم اختيار مورد"}
                      readOnly
                      style={{ ...inputStyle, flex: 1, background: "#f8fafc", cursor: "default" }}
                    />
                    <button
                      type="button"
                      onClick={() => openSupplierModal(index)}
                      style={{
                        background: "#2563eb",
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        border: "none",
                        fontWeight: "600",
                        fontSize: "14px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <i className="bi bi-search me-1"></i> اختيار
                    </button>
                  </div>
                  {formik.touched.items?.[index]?.supplierId &&
                    formik.errors.items?.[index]?.supplierId && (
                      <div style={errorStyle}>{formik.errors.items[index].supplierId}</div>
                    )}
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label style={labelStyle}>المبلغ (جنيه)</label>
                    <input
                      name={`items[${index}].amount`}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="أدخل المبلغ"
                      value={item.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={inputStyle}
                    />
                    {formik.touched.items?.[index]?.amount &&
                      formik.errors.items?.[index]?.amount && (
                        <div style={errorStyle}>{formik.errors.items[index].amount}</div>
                      )}
                  </div>

                  <div className="col-md-6">
                    <CustomDatePicker
                      label="التاريخ"
                      value={item.balanceDate}
                      onChange={(date) => formik.setFieldValue(`items[${index}].balanceDate`, date)}
                      onBlur={() => formik.setFieldTouched(`items[${index}].balanceDate`, true)}
                      placeholder="اختر التاريخ"
                      error={formik.touched.items?.[index]?.balanceDate && formik.errors.items?.[index]?.balanceDate}
                    />
                    {formik.touched.items?.[index]?.balanceDate &&
                      formik.errors.items?.[index]?.balanceDate && (
                        <div style={errorStyle}>{formik.errors.items[index].balanceDate}</div>
                      )}
                  </div>

                  <div className="col-md-12">
                    <label style={labelStyle}>ملاحظات (اختياري)</label>
                    <textarea
                      name={`items[${index}].notes`}
                      placeholder="أضف أي ملاحظات..."
                      rows="3"
                      value={item.notes}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        ...inputStyle,
                        resize: "vertical",
                        minHeight: "80px",
                      }}
                    />
                    {formik.touched.items?.[index]?.notes &&
                      formik.errors.items?.[index]?.notes && (
                        <div style={errorStyle}>{formik.errors.items[index].notes}</div>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* زر الحفظ */}
          <button
            type="submit"
            disabled={!(formik.dirty && formik.isValid)}
            style={{
              width: "100%",
              background: formik.dirty && formik.isValid 
                ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" 
                : "#94a3b8",
              color: "#fff",
              padding: "16px",
              borderRadius: "8px",
              border: "none",
              fontWeight: "700",
              fontSize: "18px",
              cursor: formik.dirty && formik.isValid ? "pointer" : "not-allowed",
              boxShadow: formik.dirty && formik.isValid 
                ? "0 4px 12px rgba(34, 197, 94, 0.3)" 
                : "none",
              transition: "all 0.3s ease",
            }}
          >
            <i className="bi bi-check-circle me-2"></i> حفظ الرصيد الافتتاحي
          </button>
        </form>
      </div>

      {/* 🎯 نافذة اختيار المورد */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "700px",
              maxHeight: "80vh",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* رأس النافذة */}
            <div style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 style={{ color: "#fff", fontWeight: "700", fontSize: "20px", margin: 0 }}>
                  <i className="bi bi-search me-2"></i>اختيار مورد
                </h5>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    cursor: "pointer",
                    fontSize: "20px",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* محتوى النافذة */}
            <div style={{ padding: "20px 24px" }}>
              <input
                type="text"
                placeholder="🔍 ابحث عن الموردين بالاسم أو الهاتف..."
                value={supplierSearch}
                onChange={(e) => setSupplierSearch(e.target.value)}
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #bfdbfe",
                  borderRadius: "8px",
                  fontSize: "15px",
                  marginBottom: "16px",
                  outline: "none",
                }}
              />

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {loadingSuppliers ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <div className="spinner-border text-primary" role="status"></div>
                    <p style={{ marginTop: "12px" }}>جارٍ تحميل الموردين...</p>
                  </div>
                ) : suppliers.length > 0 ? (
                  <div>
                    {suppliers.map((supplier) => (
                      <button
                        key={supplier.id}
                        type="button"
                        onClick={() => selectSupplier(supplier)}
                        style={{
                          width: "100%",
                          padding: "16px",
                          marginBottom: "8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          background: "#fff",
                          textAlign: "left",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0f9ff";
                          e.currentTarget.style.borderColor = "#2563eb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                      >
                        <div style={{ fontWeight: "600", color: "#1e293b", marginBottom: "4px" }}>
                          {supplier.name}
                        </div>
                        <div style={{ fontSize: "14px", color: "#64748b" }}>
                          📱 {supplier.phone} {supplier.details && `• ${supplier.details}`}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <i className="bi bi-inbox" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>لا توجد موردين</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ✅ الأنماط
const cardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
};

const sectionTitleStyle = {
  color: "#1e293b",
  fontWeight: "700",
  fontSize: "18px",
  marginBottom: "20px",
};

const labelStyle = {
  color: "#475569",
  fontSize: "14px",
  fontWeight: "600",
  marginBottom: "6px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  fontSize: "15px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "13px",
  marginTop: "4px",
  fontWeight: "500",
};

const itemCardStyle = {
  background: "#f8fafc",
  border: "2px solid #e0e7ff",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "12px",
};